import { useEffect, useRef, useState } from "react";
import { DropResult } from "@hello-pangea/dnd";
import { deleteHttp, post, put } from "../../common/api/apiShared";
import { useOptimisticUpdate } from "../../common/hooks/useOptimisticUpdate";
import { ISubTasksProps } from "./SubTasks";
import { SubTasksViewModel, SubTaskViewModel } from "./SubTasks.types";
import { isNullOrWhitespace } from "../../common/stringOperations";
import { getRelationsUrl } from "../../common/hooks/Relations.hooks.ts";
import { containsMention } from "../../common/utilities.ts";
import { useDotLegalQueryClient, useDotLegalQueryWithKey } from "@common/hooks/dotLegalReactQuery";

export function useSubTasksHook(props: ISubTasksProps) {
    const queryClient = useDotLegalQueryClient();
    const optimisticUpdate = useOptimisticUpdate();
    const url = `/subtask/${props.taskId}`;
    const queryKey = "subtask" + props.taskId;

    const [hoveredSubTask, setHoveredSubTask] = useState("");
    const [focusOnLastSubTask, setFocusOnLastSubTask] = useState(false);
    const lastSubTaskRef = useRef<HTMLDivElement>(null);

    const { isLoading, data, isSuccess } = useDotLegalQueryWithKey<SubTasksViewModel>(queryKey, url);

    useEffect(() => {
        if (props.passIsDoneLoading && isSuccess) {
            props.passIsDoneLoading(true);
        }
    }, [isSuccess, props]);

    useEffect(() => {
        if (props.shouldAddTask) {
            addSubTask();
            if (props.onSuccessAddTask) {
                props.onSuccessAddTask();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.shouldAddTask]);

    async function addSubTask() {
        if (data) {
            if (data.subTasks.length === 0 || !isNullOrWhitespace(data.subTasks[data.subTasks.length - 1].description)) {
                await optimisticUpdate.putOnQueueAndSetQueryData(data, queryKey, add, undefined);
            }
        }

        setFocusOnLastSubTask(true);
        // Focusing the editor does not bring it into view in the collapsed panel, where the subtasks sit below
        // Details and Relations. The frame lets the new row commit before it is scrolled to.
        requestAnimationFrame(() => lastSubTaskRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }));
    }

    async function removeSubTask(id: string) {
        let tempData = { ...data };
        tempData.subTasks = data!.subTasks!.filter((d) => d.id !== id);
        await optimisticUpdate.putOnQueueAndSetQueryData(tempData, queryKey, remove, id);
    }

    async function onSubTaskChange(taskViewModels: SubTasksViewModel, taskViewModel: SubTaskViewModel) {
        await optimisticUpdate.putOnQueueAndSetQueryData(taskViewModels, queryKey, updateSubTask, taskViewModel);
    }

    async function updateDescription(newName: string | null, id: string) {
        let tempData = { ...data! };

        const foundTask = tempData.subTasks.find((x) => x.id === id);
        foundTask!.description = newName!;

        await onSubTaskChange(tempData, foundTask!);
        if (containsMention(foundTask!.description)) {
            queryClient.refetchQueries(getRelationsUrl(props.taskId));
        }
    }

    async function handleDragEnd(result: DropResult) {
        setFocusOnLastSubTask(false);
        (document.activeElement as HTMLElement)?.blur();

        if (!result.destination) {
            return;
        }

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) {
            return;
        }

        const sourceItem = data!.subTasks[sourceIndex];
        const destinationItem = data!.subTasks[destinationIndex];

        let tempData = { ...data! };
        const newSubTasks = [...tempData.subTasks];
        const [removed] = newSubTasks.splice(sourceIndex, 1);
        newSubTasks.splice(destinationIndex, 0, removed);
        tempData.subTasks = newSubTasks;

        await optimisticUpdate.putOnQueueAndSetQueryData(tempData, queryKey, updateSubTaskOrder, {
            sourceId: sourceItem.id,
            destinationId: destinationItem.id,
        });
    }

    return {
        isLoading,
        data,
        onSubTaskChange,
        removeSubTask,
        hoveredSubTask,
        setHoveredSubTask,
        addSubTask,
        focusOnLastSubTask,
        lastSubTaskRef,
        updateDescription,
        handleDragEnd,
    };

    async function updateSubTask(viewModel: SubTaskViewModel) {
        return await put<Array<SubTaskViewModel>>("/subtask/" + props.taskId + "/" + viewModel.id, convertToSaveModel(viewModel));
    }

    async function remove(id: string) {
        return await deleteHttp<Array<SubTaskViewModel>>(`/subtask/${props.taskId}/${id}`);
    }

    async function add() {
        return post<Array<SubTaskViewModel>>("/subtask/" + props.taskId, null);
    }

    async function updateSubTaskOrder(orderData: { sourceId: string; destinationId: string }) {
        return await put<Array<SubTaskViewModel>>(`/subtask/${props.taskId}/updateorder`, orderData);
    }

    function convertToSaveModel(viewModel: SubTaskViewModel) {
        return {
            id: viewModel.id,
            description: viewModel.description,
            isComplete: viewModel.isComplete,
        };
    }
}
